// AI Office — opened from the "AI Office" link in the navigation bar, in its own window, at /ai-office.
// This page renders only AI Office: no NavigationBar and none of the web app's Bootstrap/MUI look. The design is
// ai-office-prototype.html; its CSS is ported into aiOffice.css (scoped under .aio).
//
// The office shows the real employees from GET /ai-office/state, kept live over /ai-office/events (SSE).
// Chat answers stream in over the same events. Anything that changes data comes as an approval card (in the
// chat and in Approvals): Approve / Change / Reject, then the employee reports back. Office life: now and then two
// free employees have a coffee or a quick chat (useOfficeLife). See PROGRESS.md for what comes next.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './aiOffice.css';
import './aiOfficeLayout.css';
import Avatar from './Avatar';
import { statusOf } from './pixel';
import OfficeCanvas from './office/OfficeCanvas';
import ChatDrawer from './chat/ChatDrawer';
import Approvals from './views/Approvals';
import TaskBoard from './views/TaskBoard';
import History from './views/History';
import Profile from './views/Profile';
import Settings from './views/Settings';
import Hire from './views/Hire';
import MorningBrief from './office/MorningBrief';
import useOfficeLife from './office/useOfficeLife';
import { applyChatEvent, applyApprovalUpdate, CHAT_EVENTS } from './chat/thread';
import { getState, getConversation, sendMessage, openEvents, approve, reject, startBrief, errorText, voiceTurn } from './api';
import { usd } from './fmt';
import { useListen, speakReply, stopSpeaking } from './useVoice';
import useGeminiLive, { liveCallSupported } from './useGeminiLive';

const FONTS_HREF = 'https://fonts.googleapis.com/css2?family=Figtree:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&family=Pixelify+Sans:wght@500;700&display=swap';
const POLL_MS = 30000; // safety net only: live changes arrive over /ai-office/events
const NAV = [['office', 'Office'], ['approvals', 'Approvals'], ['tasks', 'Tasks'], ['history', 'History'], ['settings', 'Settings']];
const BRIEF_MAX_MS = 120000; // the brief panel offers "Back to work" after this, even if the end never arrived
const BRIEF_SAY_MS = 2500;   // how long each person shows "Reporting" at the meeting table
const LEGEND = [['#5FB98A', 'Free'], ['#E3A33B', 'Working'], ['#59B7D6', 'Talking with you'], ['#E5566F', 'Needs you'], ['#9AA3AF', 'Away from desk']];

const readPref = (k, d) => { try { return localStorage.getItem(k) || d; } catch (_) { return d; } };
const writePref = (k, v) => { try { localStorage.setItem(k, v); } catch (_) { /* private window */ } };
const wait = (ms) => new Promise((r) => setTimeout(r, ms));
// A reject reason about timing ("Not now", "later"…): the details were fine, the boss just does not want it yet.
// Same rule as the server's (aiOffice/approvals.js isLater).
const isLater = (reason) => {
  const r = String(reason || '').trim();
  return /^(not now|not today|not yet|maybe later|hold (it|off)|leave it)\b/i.test(r) || /^(later|wait)[.!\s]*$/i.test(r);
};
const FILE_ONLY_TEXT = 'Please look at the attached file.'; // what a message with files and no words says (also on the server)
const helloFor = (a, id) => ({ kind: 'agent', agentId: id, text: (a && a.intro) || `Hi boss, I'm ${a ? a.name : 'here'}.` });

// The prototype's fonts, loaded only while AI Office is open (the rest of the web app never gets them).
function useFonts() {
  useEffect(() => {
    if (document.querySelector('link[data-aio-fonts]')) return;
    const l = document.createElement('link');
    l.rel = 'stylesheet'; l.href = FONTS_HREF; l.dataset.aioFonts = '1';
    document.head.appendChild(l);
  }, []);
}

export default function AIOfficeApp() {
  useFonts();
  const [office, setOffice] = useState(null); // GET /ai-office/state
  const callRef = useRef(null); // employee on a voice call right now (their replies are not read aloud twice)
  const [loadError, setLoadError] = useState(null);
  const [view, setView] = useState('office');
  const [profileId, setProfileId] = useState(null);
  const [chatId, setChatId] = useState(null);
  const [threads, setThreads] = useState({}); // agentId -> messages shown in the drawer
  const [toasts, setToasts] = useState([]);
  const [speak, setSpeak] = useState(readPref('aio-speak', '0') === '1');
  const [theme, setTheme] = useState(readPref('aio-theme', 'auto'));
  const [officeLife, setOfficeLife] = useState(readPref('aio-life', '1') === '1');
  const [busy, setBusy] = useState({}); // agentId -> true while they are answering
  const [approvalsVersion, setApprovalsVersion] = useState(0); // bumps when any approval changes
  const [jobsVersion, setJobsVersion] = useState(0);           // bumps when the task board changes
  const [brief, setBrief] = useState(null);                     // { lines, done, leaving } while the morning brief is on
  const entering = useRef(null);                                // a new hire who should walk in from the door
  const canvasRef = useRef(null);
  const [listenLang, setListenLang] = useState(readPref('aio-listen-lang', 'bn-BD'));
  const [speakingId, setSpeakingId] = useState(null); // employee whose reply is being read aloud
  const voiceAsked = useRef(new Set());  // employees the boss just spoke to by voice: their answer is read aloud
  const noBanglaWarned = useRef(false);
  const sendRef = useRef(null);       // latest send(), for the mic
  const listenFor = useRef(null);     // whose chat the mic was started in (the words go only to them)
  const stageRef = useRef(null);
  const now = useRef({ speak, chatId, office, brief, busy });
  now.current = { speak, chatId, office, brief, busy };

  useEffect(() => { document.title = 'AI Office'; }, []);

  const refresh = useCallback(() => getState().then(
    (s) => { setOffice(s); setLoadError(null); },
    (e) => setLoadError(e)
  ), []);
  useEffect(() => {
    refresh();
    const t = setInterval(() => { refresh(); reconcileRef.current(); }, POLL_MS);
    return () => clearInterval(t);
  }, [refresh]);

  // Someone is answering, also on their own (a report after Approve / Reject, a hand-off): the boss waits for them.
  const lastWork = useRef({}); // agentId -> time of the last sign of work (a check never clears a run that just began)
  const sending = useRef(new Set()); // agentIds whose message is still on its way to the server
  const markBusy = useCallback((id) => {
    lastWork.current[id] = Date.now();
    setBusy((b) => (b[id] ? b : { ...b, [id]: true }));
  }, []);
  // If the end of a run was missed (the server restarted, the connection dropped), ask the server who is really
  // still working, and show the conversation as the server has it.
  const reconcileRef = useRef(null);
  reconcileRef.current = () => {
    for (const id of Object.keys(now.current.busy)) {
      const since = lastWork.current[id] || 0;
      if (!now.current.busy[id] || sending.current.has(id) || Date.now() - since < 5000) continue;
      getConversation(id).then((c) => {
        if (c.running || !now.current.busy[id] || sending.current.has(id) || lastWork.current[id] !== since) return;
        const a = now.current.office && now.current.office.agents.find((x) => x.id === id);
        loadedRef.current.add(id);
        setBusy((b) => ({ ...b, [id]: false }));
        setThreads((t) => ({ ...t, [id]: [helloFor(a, id), ...(c.messages || [])] }));
      }, () => {});
    }
  };

  // Live events: office status lights / bubbles, and the chat stream.
  useEffect(() => openEvents((ev) => {
    if (ev.type === 'office') {
      setOffice((o) => (o ? { ...o, agents: o.agents.map((a) => (a.id === ev.agentId ? { ...a, status: ev.status, bubble: ev.bubble, progress: ev.progress } : a)) } : o));
      return;
    }
    if (ev.type === 'approvals_changed') { setApprovalsVersion((v) => v + 1); refresh(); return; }
    if (ev.type === 'jobs_changed') { setJobsVersion((v) => v + 1); refresh(); return; }
    if (ev.type === 'staff_changed') { refresh(); return; }
    if (ev.type === 'brief_line') { briefLineRef.current(ev); return; }
    if (ev.type === 'brief_done') {
      setBrief((b) => (b ? { ...b, done: true } : b));
      if (ev.error && now.current.brief) toastRef.current(ev.error);
      return;
    }
    if (ev.type === 'approval_decided') {
      setThreads((t) => Object.fromEntries(Object.entries(t).map(([k, msgs]) => [k, applyApprovalUpdate(msgs, ev.approval)])));
      return;
    }
    if (ev.type === 'handoff') { handoffRef.current(ev); return; }
    if (ev.type === 'typing' || ev.type === 'text' || ev.type === 'tool_start') { markBusy(ev.agentId); handoffIdle.current.delete(ev.agentId); }
    // A hand-off is over when the colleague or the asker finishes (or fails): the asker walks back.
    if (ev.type === 'done' || ev.type === 'error') {
      for (const [askerId, w] of waitersRef.current) {
        if (w.to === ev.agentId || askerId === ev.agentId) { waitersRef.current.delete(askerId); w.resolve(); }
      }
      // The asker finished but the colleague never started on the question (it was turned down): they are free.
      handoffIdle.current.delete(ev.agentId);
      for (const [colleagueId, askerId] of handoffIdle.current) {
        if (askerId !== ev.agentId) continue;
        handoffIdle.current.delete(colleagueId);
        setBusy((b) => (b[colleagueId] ? { ...b, [colleagueId]: false } : b));
      }
    }
    if (!CHAT_EVENTS.has(ev.type)) return;
    setThreads((t) => ({ ...t, [ev.agentId]: applyChatEvent(t[ev.agentId] || [], ev) }));
    if (ev.type === 'done' || ev.type === 'error') {
      lastWork.current[ev.agentId] = Date.now();
      setBusy((b) => ({ ...b, [ev.agentId]: false }));
      const open = now.current.chatId === ev.agentId;
      // After a hand-off the boss is already reading the colleague's answer: no extra note from the asker.
      const via = handoffTo.current.get(ev.agentId);
      handoffTo.current.delete(ev.agentId);
      const watchingColleague = via && now.current.chatId === via;
      if (ev.type === 'done' && open && ev.text && callRef.current !== ev.agentId && (now.current.speak || voiceAsked.current.has(ev.agentId))) readAloudRef.current(ev.agentId, ev.text);
      if (ev.type === 'done' && !ev.asked) voiceAsked.current.delete(ev.agentId);
      // An employee finished (a long job, a report after an approval…) while their chat is closed: tap the boss.
      if (ev.type === 'done' && !open && !watchingColleague && ev.text && !ev.asked) {
        const a = now.current.office && now.current.office.agents.find((x) => x.id === ev.agentId);
        const line = ev.text.split('\n')[0];
        toastRef.current(`${a ? a.name : 'Employee'}: ${line.length > 90 ? `${line.slice(0, 88)}…` : line}`, ['Open', () => openChatRef.current(ev.agentId)]);
      }
      refresh();
    }
  }, (up) => {
    if (!up) return;
    // Back online: catch up on anything missed while the connection was down.
    refresh();
    reconcileRef.current();
    setBrief((b) => (b && !b.done ? { ...b, done: true } : b));
  }), [refresh, markBusy]);

  const agents = useMemo(() => (office ? office.agents : []), [office]);
  const agentsById = useMemo(() => Object.fromEntries(agents.map((a) => [a.id, a])), [agents]);

  const toast = useCallback((text, action) => {
    const id = Math.random().toString(36).slice(2);
    setToasts((t) => [...t, { id, text, action }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 4200);
  }, []);

  const toastRef = useRef(toast);
  toastRef.current = toast;
  const go = (v) => { setView(v); if (v !== 'profile') setProfileId(null); };

  const life = useOfficeLife({ canvasRef, agents, chatId, active: officeLife && view === 'office' && !!office && !brief });

  // Talking: the mic sends what was heard as the boss's message ("Said by voice"); the answer is read aloud.
  // The words go only to the employee whose chat the mic was started in.
  const listen = useListen({
    lang: listenLang,
    onFinal: (text) => {
      const id = now.current.chatId;
      if (id && id === listenFor.current) { voiceAsked.current.add(id); sendRef.current(text, true); }
    },
    onError: (msg) => toast(msg),
  });
  const cancelListen = listen.cancel; // stable
  const startListen = (id) => { listenFor.current = id; stopSpeaking(); listen.start(); };
  const closeChat = () => { cancelListen(); stopSpeaking(); setChatId(null); };

  // ---- Morning brief: everyone walks to the meeting table; once they are all there, each one in turn shows
  // "Reporting" while their line (from real data) appears in the brief panel. ----
  const briefRun = useRef(null);   // { queue, wake, stopped } of the brief that is on
  const briefTimer = useRef(null);
  const briefLineRef = useRef(null);
  briefLineRef.current = (ev) => {
    setBrief((b) => (b ? { ...b, lines: [...b.lines.filter((l) => l.agentId !== ev.agentId), { agentId: ev.agentId, text: ev.text }] } : b));
    const run = briefRun.current;
    if (run && ev.text) {
      run.queue.push(ev);
      if (run.wake) { const w = run.wake; run.wake = null; w(); }
    }
  };
  // One person at a time at the table (read aloud too when that is on), so the bubbles never pile up.
  const playBrief = async (run, arrived) => {
    await arrived;
    while (!run.stopped) {
      const l = run.queue.shift();
      if (!l) { await new Promise((r) => { run.wake = r; }); continue; }
      const cv = canvasRef.current;
      if (cv) cv.say(l.agentId, 'Reporting');
      const spoken = now.current.speak ? new Promise((r) => readAloudRef.current(l.agentId, l.text, r)) : null;
      await Promise.all([wait(BRIEF_SAY_MS), spoken && Promise.race([spoken, wait(30000)])]);
      if (cv) cv.say(l.agentId, null);
    }
  };
  const runBrief = async () => {
    if (brief) return;
    closeChat();
    setBrief({ lines: [], done: false, leaving: false });
    const cv = canvasRef.current;
    const walks = agents.map((a, i) => { life.interrupt(a.id); return cv ? wait(i * 250).then(() => cv.walkTo(a.id, 'meet')) : null; });
    const run = { queue: [], wake: null, stopped: false };
    briefRun.current = run;
    playBrief(run, Promise.race([Promise.all(walks), wait(15000)]));
    clearTimeout(briefTimer.current);
    briefTimer.current = setTimeout(() => setBrief((b) => (b && !b.done ? { ...b, done: true } : b)), BRIEF_MAX_MS);
    try {
      await startBrief();
    } catch (err) {
      toast(errorText(err));
      setBrief((b) => (b ? { ...b, done: true } : b));
    }
  };
  // "Back to work" / "Stop waiting": everyone walks back, and the panel closes once they are seated.
  // leaveNow: the boss is going to another screen, so there is nothing to wait for.
  const endBrief = async (leaveNow = false) => {
    const b = now.current.brief;
    if (!b || b.leaving) return;
    clearTimeout(briefTimer.current);
    const run = briefRun.current;
    briefRun.current = null;
    if (run) { run.stopped = true; run.queue.length = 0; if (run.wake) { const w = run.wake; run.wake = null; w(); } }
    if (now.current.speak) stopSpeaking();
    const cv = canvasRef.current;
    if (cv) agents.forEach((a) => cv.say(a.id, null));
    if (leaveNow || !cv) { setBrief(null); return; }
    setBrief((x) => (x ? { ...x, leaving: true } : x)); // keeps the brief button off and office life paused
    const walks = Promise.all(agents.map((a, i) => wait(i * 200).then(() => cv.walkTo(a.id, 'desk'))));
    await Promise.race([walks, wait(10000)]);
    setBrief(null);
    toast('Everyone is back at their desk.');
  };
  useEffect(() => () => clearTimeout(briefTimer.current), []);
  // Back on the office screen during the brief (after another tab): the new picture starts with everyone at their
  // desks, so send them to the table again.
  useEffect(() => {
    const cv = canvasRef.current, b = now.current.brief;
    if (view !== 'office' || !cv || !b || b.leaving) return;
    agents.forEach((a, i) => setTimeout(() => cv.walkTo(a.id, 'meet'), i * 250));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  // ---- A new hire walks in from the door once the office knows about them. ----
  useEffect(() => {
    const id = entering.current;
    if (!id || !agents.some((a) => a.id === id) || !canvasRef.current) return;
    entering.current = null;
    const cv = canvasRef.current;
    cv.setLocal(id, { say: 'Hello!' });
    cv.enterFromDoor(id).then(() => cv.say(id, null));
  }, [agents]);

  // Loads today's conversation with this employee once (it continues where it left off). Lines that arrived live
  // BEFORE loading started are already in the server's copy (unless that employee is still working); lines that
  // arrive while it loads are kept.
  const loadedRef = useRef(new Set());
  const loadThread = useCallback((id) => {
    const a = agentsById[id];
    if (!a || loadedRef.current.has(id)) return;
    loadedRef.current.add(id);
    const hello = helloFor(a, id);
    let before = 0;
    setThreads((t) => { before = (t[id] || []).length; return { ...t, [id]: [hello, ...(t[id] || [])] }; });
    getConversation(id).then((c) => {
      const earlier = c.messages || [];
      setThreads((t) => {
        const live = (t[id] || []).slice(1);
        return { ...t, [id]: [hello, ...earlier, ...(c.running ? live : live.slice(before))] };
      });
      if (c.running) setBusy((b) => ({ ...b, [id]: true }));
    }, () => {});
  }, [agentsById]);

  const openChat = useCallback((id) => {
    if (!agentsById[id]) return;
    // Switching to someone else: stop the mic and the reading aloud, so nothing goes to the wrong person.
    if (id !== now.current.chatId) { cancelListen(); stopSpeaking(); }
    life.interrupt(id);
    setView('office'); setProfileId(null); setChatId(id);
    loadThread(id);
    reconcileRef.current();
  }, [agentsById, life, loadThread, cancelListen]);

  const openChatRef = useRef(openChat);
  openChatRef.current = openChat;

  // Hand-off (ask_colleague): the asker walks to the colleague's desk, the chat switches to the colleague (who
  // answers there), then the asker walks back once the colleague is done.
  const waitersRef = useRef(new Map()); // askerId -> { to: colleagueId, resolve } until the hand-off is over
  const handoffTo = useRef(new Map());  // askerId -> colleagueId, until the asker finishes
  const handoffIdle = useRef(new Map()); // colleagueId -> askerId, until the colleague shows any sign of work
  const handoffRef = useRef(null);
  handoffRef.current = (ev) => {
    const from = agentsById[ev.agentId], to = agentsById[ev.to];
    if (!from || !to) return;
    loadThread(to.id);
    setThreads((t) => ({
      ...t,
      [from.id]: [...(t[from.id] || []).filter((m) => m.kind !== 'typing'), { kind: 'sys', text: `${from.name} is taking your question to ${to.name}…` }, { kind: 'typing', agentId: from.id }],
      [to.id]: [...(t[to.id] || []), { kind: 'sys', text: `${from.name} brought your question: “${ev.question}”` }],
    }));
    if (!now.current.busy[to.id]) handoffIdle.current.set(to.id, from.id);
    markBusy(to.id); // the colleague answers in their own chat now
    handoffTo.current.set(from.id, to.id);
    const cv = canvasRef.current;
    life.interrupt(from.id); life.interrupt(to.id);
    const colleagueDone = new Promise((resolve) => {
      const w = { to: to.id, resolve };
      const old = waitersRef.current.get(from.id);
      if (old) old.resolve();
      waitersRef.current.set(from.id, w);
      setTimeout(() => { if (waitersRef.current.get(from.id) === w) waitersRef.current.delete(from.id); resolve(); }, 60000);
    });
    if (cv) cv.walkTo(from.id, { beside: to.desk }).then(() => colleagueDone).then(() => cv.walkTo(from.id, 'desk'));
    if (now.current.chatId === from.id) setTimeout(() => { if (now.current.chatId === from.id) openChatRef.current(to.id); }, 1500);
  };

  // "Try again" after the AI service failed: send the words of the boss's last message again, without its files
  // (a file the AI could not read would only fail again). A message that was only files has nothing to resend.
  const lastBoss = (id) => [...(threads[id] || [])].reverse().find((m) => m.kind === 'boss');
  const canRetry = (id) => {
    const last = lastBoss(id);
    return !!last && !!last.text && !(last.text === FILE_ONLY_TEXT && last.attachments && last.attachments.length);
  };
  const retry = (id) => { if (canRetry(id)) send(lastBoss(id).text, false); };

  // Approve / Change / Reject from a card (in the chat or the Approvals inbox). Throws a readable error so the
  // card can show it (e.g. an edited value that doesn't fit).
  const decide = useCallback(async (item, decision) => {
    try {
      const r = decision.choice === 'approve' ? await approve(item.id, decision.edited) : await reject(item.id, decision.reason);
      if (r && r.approval) setThreads((t) => Object.fromEntries(Object.entries(t).map(([k, msgs]) => [k, applyApprovalUpdate(msgs, r.approval)])));
      setApprovalsVersion((v) => v + 1);
      markBusy(item.agentId); // they report back now; the boss waits for that before writing again
      const a = agentsById[item.agentId];
      const name = a ? a.name : 'They';
      if (now.current.chatId !== item.agentId) {
        toast(decision.choice === 'approve'
          ? (r.approval && r.approval.status === 'failed' ? `Approved, but it did not go through. ${name} will explain.` : `Approved. ${name} is reporting back.`)
          : isLater(decision.reason) ? `Rejected. ${name} will put it aside for now.` : `Rejected. ${name} will look again.`);
      }
      refresh();
      return r;
    } catch (err) {
      setApprovalsVersion((v) => v + 1);
      throw new Error(errorText(err));
    }
  }, [agentsById, toast, refresh, markBusy]);

  const send = (text, viaVoice, files = []) => {
    const id = chatId;
    if (!id || busy[id]) return;
    const attachments = files.map((f) => ({ name: f.name, size: f.size, ext: f.ext }));
    setThreads((t) => ({ ...t, [id]: [...(t[id] || []), { kind: 'boss', text: text || 'Please look at the attached file.', viaVoice, attachments }, { kind: 'typing', agentId: id }] }));
    markBusy(id);
    sending.current.add(id);
    sendMessage(id, text, viaVoice, files).catch((err) => {
      setThreads((t) => ({ ...t, [id]: [...(t[id] || []).filter((m) => m.kind !== 'typing'), { kind: 'sys', err: true, text: errorText(err) }] }));
      setBusy((b) => ({ ...b, [id]: false }));
    }).finally(() => sending.current.delete(id));
  };
  sendRef.current = send;

  // Voice call (Gemini Live). What the employee works on during the call shows in the chat like a typed message;
  // Gemini says the answer out loud, so the browser's read-aloud stays quiet for that employee.
  const [captions, setCaptions] = useState({ boss: '', agent: '' });
  const live = useGeminiLive({
    onWork: async (agentId, request) => {
      setThreads((t) => ({ ...t, [agentId]: [...(t[agentId] || []), { kind: 'boss', text: request, viaVoice: true }, { kind: 'typing', agentId }] }));
      markBusy(agentId);
      try {
        return await voiceTurn(agentId, request);
      } catch (err) {
        setThreads((t) => ({ ...t, [agentId]: (t[agentId] || []).filter((m) => m.kind !== 'typing') }));
        setBusy((b) => ({ ...b, [agentId]: false }));
        return { result: `It did not work: ${errorText(err)}` };
      }
    },
    onCaption: (agentId, who, text) => setCaptions((c) => ({ ...c, [who]: text })),
    onEnded: (agentId, reason) => { setCaptions({ boss: '', agent: '' }); if (reason) toast(reason); },
  });
  callRef.current = live.call.agentId;
  // Switching to another employee or closing the chat ends the call.
  useEffect(() => { if (live.call.agentId && live.call.agentId !== chatId) live.end(); }, [chatId, live]);

  const showProfile = (id) => { closeChat(); setProfileId(id); setView('profile'); };
  // Reads a reply aloud in the employee's voice style. Without a Bangla voice on this computer, Bangla replies stay
  // text only (said once).
  const readAloudRef = useRef(null);
  readAloudRef.current = (agentId, text, onDone) => {
    const a = now.current.office && now.current.office.agents.find((x) => x.id === agentId);
    const ok = speakReply(text, a && a.voice, { onStart: () => setSpeakingId(agentId), onEnd: () => { setSpeakingId((x) => (x === agentId ? null : x)); if (onDone) onDone(); } });
    if (!ok && onDone) onDone();
    if (!ok && !noBanglaWarned.current) {
      noBanglaWarned.current = true;
      toast('This computer has no Bangla voice for reading aloud, so Bangla replies are shown as text only.');
    }
  };
  const setListenLangPref = (v) => { setListenLang(v); writePref('aio-listen-lang', v); };

  const setSpeakPref = (v) => { setSpeak(v); writePref('aio-speak', v ? '1' : '0'); if (v) toast('Replies will be read aloud.'); };
  const setThemePref = (v) => { setTheme(v); writePref('aio-theme', v); };
  const setLifePref = (v) => { setOfficeLife(v); writePref('aio-life', v ? '1' : '0'); };

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') { cancelListen(); stopSpeaking(); setChatId(null); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [cancelListen]);

  // On a phone the chat covers the whole screen, so the phone's Back button should close it (not leave AI Office).
  const chatInHistory = useRef(false);
  useEffect(() => {
    const narrow = window.matchMedia && window.matchMedia('(max-width:860px)').matches;
    if (chatId && narrow && !chatInHistory.current) {
      window.history.pushState({ ...(window.history.state || {}), aioChat: true }, '');
      chatInHistory.current = true;
    } else if (!chatId && chatInHistory.current) {
      chatInHistory.current = false;
      window.history.back(); // closed some other way (X, Escape, profile…)
    }
  }, [chatId]);
  useEffect(() => {
    const onPop = () => {
      if (!chatInHistory.current) return;
      chatInHistory.current = false;
      cancelListen(); stopSpeaking(); setChatId(null);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [cancelListen]);

  // A new screen starts at the top (the screens share one scrolling area).
  useEffect(() => { if (stageRef.current) stageRef.current.scrollTop = 0; }, [view, profileId]);

  // The page behind AI Office takes its colours too (no white edges or flash; see public/index.html).
  useEffect(() => {
    const el = document.documentElement;
    const meta = document.querySelector('meta[name="theme-color"]');
    const metaWas = meta ? meta.getAttribute('content') : null;
    const mq = window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null;
    const apply = () => {
      const dark = theme === 'dark' || (theme !== 'light' && !!mq && mq.matches);
      const bg = dark ? '#12151B' : '#E8EBEE';
      el.style.background = bg;
      el.style.colorScheme = dark ? 'dark' : 'light';
      if (meta) meta.setAttribute('content', bg);
    };
    apply();
    if (mq && mq.addEventListener) mq.addEventListener('change', apply);
    return () => {
      if (mq && mq.removeEventListener) mq.removeEventListener('change', apply);
      el.style.background = ''; el.style.colorScheme = '';
      if (meta && metaWas != null) meta.setAttribute('content', metaWas);
    };
  }, [theme]);

  // Live status for the open chat's header comes from the same polled state.
  const chatAgent = chatId ? agentsById[chatId] : null;
  const counts = office ? office.counts : { approvals: 0, working: 0, doneToday: 0, costTodayUSD: 0 };
  const boss = office ? office.boss : { initials: '' };
  // Status lights: "Talking with you" only while that employee's chat is open; someone answering with the chat
  // closed (a long job, a report after an approval) is working. During the brief the server's status is used.
  const briefOn = !!brief;
  const shownAgents = useMemo(() => agents.map((a) => {
    if (a.id === chatId) return a.status === 'idle' || a.status === 'away' ? { ...a, status: 'talking' } : a;
    if (a.status === 'talking' && !briefOn) return { ...a, status: 'working' };
    return a;
  }), [agents, chatId, briefOn]);
  const manager = agents.find((a) => a.id === 'sumi') || agents[0]; // the phone's big mic talks to him (Sabbir)
  const deskFree = [0, 1, 2, 3, 4, 5].some((d) => !agents.some((a) => a.desk === d));

  return (
    <div className="aio" data-theme={theme === 'auto' ? undefined : theme}>
      <div className="app">
        <header className="top">
          <div className="logo"><i />AI OFFICE</div>
          <nav className="aio-nav">
            {NAV.map(([v, label]) => (
              <button key={v} className={view === v ? 'on' : ''} onClick={() => go(v)}>
                {label}{v === 'approvals' && counts.approvals > 0 && <span className="aio-badge">{counts.approvals}</span>}
              </button>
            ))}
          </nav>
          <div className="boss"><div className="face">{boss.initials}</div><span>Boss</span></div>
        </header>

        <div className={`work ${chatAgent ? 'chat-open' : ''}`}>
          <main className="stage" ref={stageRef}>
            {loadError && !office && <div className="err">{errorText(loadError)}</div>}
            {!office && !loadError && <div className="loading">OPENING THE OFFICE…</div>}

            {office && view === 'office' && (
              <section className="office-wrap">
                <OfficeCanvas
                  ref={canvasRef}
                  agents={shownAgents}
                  chatId={chatId}
                  onAgentClick={openChat}
                  onFreeDesk={() => go('hire')}
                />
                {/* Phone: a list of everyone instead of tapping the small picture (prototype phone scene). */}
                <div className="plist">
                  {counts.approvals > 0 && <button className="banner" onClick={() => go('approvals')}>{counts.approvals} {counts.approvals === 1 ? 'thing needs' : 'things need'} your OK →</button>}
                  {shownAgents.map((a) => {
                    const st = a.status || 'idle';
                    return (
                      <button key={a.id} className="prow" onClick={() => openChat(a.id)}>
                        <Avatar agent={a} />
                        <div><b>{a.name}</b><span>{a.role}</span></div>
                        <span className={`pill ${st}`}><i />{statusOf(st).t}</span>
                      </button>
                    );
                  })}
                  {deskFree && <button className="prow hire" onClick={() => go('hire')}>Free desk · Hire someone</button>}
                </div>
                <div className="legend">
                  {LEGEND.map(([c, t]) => <span key={t}><i style={{ background: c }} />{t}</span>)}
                  <div className="legend-end">
                    {life.theme && officeLife && <span className="vibe" title="Today's office mood, new every day">Today: {life.theme}</span>}
                    {life.badge && officeLife && <span className="vibe" title="Your title today, from how often you caught them">{life.badge}</span>}
                    {life.actor && officeLife && <span className="vibe" title="Pretended to work the most when caught">🏆 Actor of the day: {life.actor.name}</span>}
                    {life.caught > 0 && <span className="caught" title="Look away for a few seconds and they sneak off. Move the mouse to catch them!">😳 Caught slacking today: {life.caught}</span>}
                    <span className="legend-hint">Click an employee to talk to them</span>
                    <button className="aio-btn" onClick={runBrief} disabled={!!brief}>☕ Morning brief</button>
                  </div>
                </div>
                {brief && <MorningBrief lines={brief.lines} done={brief.done} leaving={!!brief.leaving} agentsById={agentsById} approvals={counts.approvals} onBack={() => endBrief()} onApprovals={() => { endBrief(true); go('approvals'); }} />}
                <div className="glance">
                  <button className="glance-link" onClick={() => go('approvals')}><b style={{ color: 'var(--wait)' }}>{counts.approvals}</b><span>Need your OK</span></button>
                  <button className="glance-link" onClick={() => go('tasks')}><b>{counts.working}</b><span>Tasks in progress</span></button>
                  <button className="glance-link" onClick={() => go('tasks')}><b>{counts.doneToday}</b><span>Done today</span></button>
                  <button className="glance-link" onClick={() => go('settings')}><b>{usd(counts.costTodayUSD)}</b><span>AI cost today</span></button>
                </div>
                {loadError && <div className="err">{errorText(loadError)}</div>}
                {manager && (
                  <div className="pfoot">
                    <button className="mic" aria-label={`Talk to ${manager.name}`} title={`Talk to ${manager.name}`} disabled={!!busy[manager.id]} onClick={() => { openChat(manager.id); startListen(manager.id); }}>
                      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0M12 18v3" /></svg>
                    </button>
                  </div>
                )}
              </section>
            )}
            {office && view === 'approvals' && <Approvals agentsById={agentsById} onBack={() => go('office')} onDecide={decide} version={approvalsVersion} />}
            {office && view === 'tasks' && <TaskBoard agentsById={agentsById} onOpenAgent={openChat} version={jobsVersion} />}
            {office && view === 'hire' && <Hire onCancel={() => go('office')} onHired={(id, name) => { entering.current = id; go('office'); refresh(); toast(`${name} joined the office.`); }} />}
            {office && view === 'history' && <History agentsById={agentsById} version={approvalsVersion} />}
            {office && view === 'settings' && <Settings speak={speak} onSpeak={setSpeakPref} listenLang={listenLang} onListenLang={setListenLangPref} theme={theme} onTheme={setThemePref} officeLife={officeLife} onOfficeLife={setLifePref} />}
            {office && view === 'profile' && profileId && <Profile agentId={profileId} onTalk={openChat} onBack={() => go('office')} onRemoved={(name) => { go('office'); refresh(); toast(`${name} left the office.`); }} onSaved={(msg) => { toast(msg); refresh(); loadedRef.current.delete(profileId); setThreads((t) => { const n = { ...t }; delete n[profileId]; return n; }); }} />}
          </main>

          {chatAgent && (
            <ChatDrawer
              agent={{ ...chatAgent, status: chatAgent.status === 'idle' ? 'talking' : chatAgent.status }}
              agentsById={agentsById}
              messages={threads[chatId] || []}
              speak={speak}
              onToggleSpeak={() => setSpeakPref(!speak)}
              onProfile={() => showProfile(chatId)}
              onClose={closeChat}
              onSend={send}
              busy={!!busy[chatId]}
              onDecide={decide}
              onAnswer={(text) => send(text, false)}
              onRetry={canRetry(chatId) ? () => retry(chatId) : null}
              onMic={() => startListen(chatId)}
              onCancelListen={cancelListen}
              listening={listen.listening}
              transcript={listen.transcript}
              listenLang={listenLang}
              onListenLang={setListenLangPref}
              speakingId={speakingId}
              onError={(msg) => toast(msg)}
              call={live.call.agentId === chatId ? live.call : null}
              captions={captions}
              onCall={liveCallSupported() ? () => { cancelListen(); stopSpeaking(); setCaptions({ boss: '', agent: '' }); live.start(chatId); } : null}
              onEndCall={() => live.end()}
              onMute={live.toggleMute}
            />
          )}
        </div>
      </div>
      <div className="aio-toasts">
        {toasts.map((t) => (
          <div className="aio-toast" key={t.id}>
            <p>{t.text}</p>
            {t.action && <button onClick={() => { setToasts((x) => x.filter((y) => y.id !== t.id)); t.action[1](); }}>{t.action[0]}</button>}
          </div>
        ))}
      </div>
    </div>
  );
}
